import React from 'react';
import { Theme } from '@mui/material';
import { ReferenceArea, ReferenceLine } from 'recharts';
import { getColor } from '../../style/utils';
import { RunStatus } from '../../types';
import { statusColorMap } from './FreshnessChartCommon';
import { MaterializationWindowRegion } from './hooks/useChartMaterialization';

type MaterializationWindowRegionShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  fillOpacity?: number;
  onRegionHover?: (event: React.MouseEvent) => void;
  onRegionLeave?: () => void;
};

// fill/fillOpacity must live on the ReferenceArea, not here - recharts overrides
// presentation props set on the shape. Healthy windows pass fill="none" so only
// the widened hit target renders (a short window's rect is too narrow to hover).
const MaterializationWindowRegionShape = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  fill,
  fillOpacity,
  onRegionHover,
  onRegionLeave,
}: MaterializationWindowRegionShapeProps) => {
  const visibleWidth = Math.max(width, 2);
  const hitWidth = Math.max(width, 12);
  const hitX = x + width / 2 - hitWidth / 2;
  // Inset the hit target below the plot's top edge so it clears the zoom
  // controls sitting just above the chart; without the gap, hovering a window
  // near the top-right corner triggers the zoom button instead of the window.
  const hitTopInset = Math.min(8, height);
  const hitY = y + hitTopInset;
  const hitHeight = Math.max(height - hitTopInset, 0);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={visibleWidth}
        height={height}
        fill={fill}
        fillOpacity={fillOpacity}
      />
      <rect
        x={hitX}
        y={hitY}
        width={hitWidth}
        height={hitHeight}
        fill="transparent"
        onMouseEnter={onRegionHover}
        onMouseMove={onRegionHover}
        onMouseLeave={onRegionLeave}
      />
    </g>
  );
};

type RenderArgs = {
  theme: Theme;
  regions: MaterializationWindowRegion[];
  status: RunStatus;
  hoveredRegionStart?: number;
  // The visible x-range (the explicit AnomalyChart domain). The shaded region is
  // drawn by a custom `shape`, and recharts does NOT clip a custom shape for
  // ifOverflow="hidden" - it hands the shape raw coordinates, so a window
  // outside the range would paint outside the plot. We clamp each region's drawn
  // span to this range (and drop windows with no visible part) so nothing spills
  // past the axes.
  viewMin: number;
  viewMax: number;
  onRegionHover: (
    region: MaterializationWindowRegion,
    event: React.MouseEvent,
  ) => void;
  onRegionLeave: () => void;
};

// Returned inline (not wrapped in a component) so recharts still recognizes
// each ReferenceArea/ReferenceLine as a direct chart child and injects the
// axis scale it needs to position them.
//
// ifOverflow="hidden" (never "extendDomain"): the chart controls the x-axis
// domain explicitly, so a region outside the visible range is clipped rather
// than stretching the axis. extendDomain would let a window outside the current
// zoom pull the domain back out and squeeze the visible line into part of the
// axis.
export const renderMaterializationWindowReferences = ({
  theme,
  regions,
  status,
  hoveredRegionStart,
  viewMin,
  viewMax,
  onRegionHover,
  onRegionLeave,
}: RenderArgs) =>
  regions.map((region) => {
    // Clamp the shaded area to the visible range - the custom shape is not
    // clipped by ifOverflow, so an unclamped x1/x2 would paint outside the plot.
    const areaStart = Math.max(region.start, viewMin);
    const areaEnd = Math.min(region.end, viewMax);
    if (areaEnd <= areaStart) return null;

    const isHovered = hoveredRegionStart === region.start;
    const regionColor = region.isAnomalous
      ? getColor(theme, statusColorMap[status === 'warn' ? 'warn' : 'fail'])
      : theme.palette.chart.idle;
    const lineOpacity = isHovered ? 0.9 : 0.6;
    return (
      <React.Fragment key={region.start}>
        <ReferenceArea
          x1={areaStart}
          x2={areaEnd}
          ifOverflow="hidden"
          fill={region.isAnomalous ? regionColor : 'none'}
          fillOpacity={region.isAnomalous ? (isHovered ? 0.4 : 0.3) : 0}
          stroke="none"
          shape={
            <MaterializationWindowRegionShape
              onRegionHover={(event: React.MouseEvent) =>
                onRegionHover(region, event)
              }
              onRegionLeave={onRegionLeave}
            />
          }
        />
        <ReferenceLine
          x={region.start}
          ifOverflow="hidden"
          stroke={regionColor}
          strokeOpacity={lineOpacity}
          strokeWidth={isHovered ? 1.5 : 1}
        />
        <ReferenceLine
          x={region.end}
          ifOverflow="hidden"
          stroke={regionColor}
          strokeOpacity={lineOpacity}
          strokeWidth={isHovered ? 1.5 : 1}
        />
      </React.Fragment>
    );
  });
